import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight, Eye, EyeOff, Lock, Mail, Moon, Sun } from 'lucide-react';
import authService from '../services/auth.service';
import '../styles/Login.css';
import nomSpotNoir from '../LogoSpot/spot-nom-fond-noir.png';
import nomSpotBlanc from '../LogoSpot/spot-nom-fond-blanc.png';
import logoSpotNoir from '../LogoSpot/spot-logo-fond-noir.png';
import logoSpotBlanc from '../LogoSpot/spot-logo-fond-blanc.png';

/**
 * Logos des entités du groupe. On choisit la bonne variante selon le thème :
 * version « blanc » (transparent) sur fond sombre, version couleur/noir sur
 * fond clair. Fichiers récupérés dans src/LogoEntity/ ; « Groupe » (le holding)
 * est écarté. Pour ajouter une entité : déposer ses variantes dans le dossier.
 */
const logoCtx = require.context('../LogoEntity', false, /\.(png|svg)$/i);
const TOUS_LOGOS = logoCtx.keys().map((cle) => ({ cle, src: logoCtx(cle) }));

const MARQUES = [
    { motif: /evolu/i },
    { motif: /galis\s*event/i },
    { motif: /galis\s*retail/i },
    { motif: /galis/i, exclure: /event|retail/i }, // Galis « seul »
    { motif: /art\s*pulsion/i },
    { motif: /clamens/i },
    { motif: /media\s*product|mp-?blanc/i },
    { motif: /factory/i },
];

/** Choisit le fichier d'une marque selon le thème (blanc si sombre, couleur/noir sinon). */
const logoDeMarque = (marque, sombre) => {
    const candidats = TOUS_LOGOS.filter(
        (l) =>
            marque.motif.test(l.cle) &&
            !/groupe/i.test(l.cle) &&
            !(marque.exclure && marque.exclure.test(l.cle))
    );
    const blancs = candidats.filter((l) => /blanc/i.test(l.cle));
    const couleurs = candidats.filter((l) => !/blanc/i.test(l.cle));
    const choix = sombre ? blancs[0] || couleurs[0] : couleurs[0] || blancs[0];
    return choix ? choix.src : null;
};

/** Répète une liste jusqu'à `min` éléments, pour couvrir la largeur du défilement. */
const remplir = (arr, min = 9) => {
    if (arr.length === 0) return arr;
    const out = [];
    while (out.length < min) out.push(...arr);
    return out;
};

const Login = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState({ email: '', mot_de_passe: '' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // Même mécanisme que la sidebar : data-theme sur <html> + localStorage.
    const [isDark, setIsDark] = useState(() => localStorage.getItem('theme') === 'dark');
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
        localStorage.setItem('theme', isDark ? 'dark' : 'light');
    }, [isDark]);

    // Police d'affichage « Plus Jakarta Sans », chargée UNIQUEMENT sur le login.
    useEffect(() => {
        const id = 'lg-font-jakarta';
        if (document.getElementById(id)) return;
        const link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap';
        document.head.appendChild(link);
    }, []);

    // Logos adaptés au thème (variante blanche sur sombre, couleur sur clair).
    const logos = useMemo(() => {
        const choisis = MARQUES.map((m) => logoDeMarque(m, isDark)).filter(Boolean);
        return remplir(choisis, 9);
    }, [isDark]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const success = await authService.login(form.email, form.mot_de_passe);
            if (success) {
                navigate('/');
            } else {
                setError('Email ou mot de passe incorrect');
            }
        } catch (err) {
            console.error('Erreur de connexion:', err);
            setError('Connexion impossible — réessayez dans un instant');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="lg-page">
            {/* ══ Scène (theme-aware) : lueur de projecteur + frise des phases ══ */}
            <section className="lg-stage" aria-hidden="true">
                <div className="lg-spot" />

                <div className="lg-stage-body">
                    <span className="lg-eyebrow">Plateforme interne · GALIS</span>
                    <h1 className="lg-wordmark">
                        <img src={isDark ? nomSpotNoir : nomSpotBlanc} alt="S.P.O.T" className="lg-wordmark-img" />
                    </h1>
                    <p className="lg-tagline">
                        La régie de vos salons et stands,<br />
                        du montage au démontage.
                    </p>
                </div>

                {/* Enseignes des exposants : logos des entités qui défilent */}
                {logos.length > 0 && (
                    <div className="lg-banners">
                        <div className="lg-ban-row">
                            {[...logos, ...logos].map((src, i) => (
                                <img key={i} src={src} alt="" className="lg-ban-logo" />
                            ))}
                        </div>
                    </div>
                )}
            </section>

            {/* ══ Panneau : le formulaire (suit le thème clair / sombre) ══ */}
            <section className="lg-panel">
                <button
                    type="button"
                    className="lg-theme-toggle"
                    onClick={() => setIsDark((v) => !v)}
                    title={isDark ? 'Passer en mode clair' : 'Passer en mode sombre'}
                    aria-label={isDark ? 'Passer en mode clair' : 'Passer en mode sombre'}
                >
                    {isDark ? <Sun size={17} /> : <Moon size={17} />}
                </button>

                <div className="lg-form-wrap">
                    <div className="lg-mini-brand">
                        <img src={isDark ? logoSpotNoir : logoSpotBlanc} alt="" className="lg-mini-logo" aria-hidden="true" />
                        <img src={isDark ? nomSpotNoir : nomSpotBlanc} alt="SPOT" className="lg-mini-nom" />
                    </div>

                    <header className="lg-head">
                        <h2 className="lg-head-title">Bon retour</h2>
                        <p className="lg-head-sub">Connectez-vous à votre espace de travail.</p>
                    </header>

                    <form className="lg-form" onSubmit={handleSubmit}>
                        {error && (
                            <div className="lg-error" role="alert">
                                <AlertCircle size={15} />
                                {error}
                            </div>
                        )}

                        <div className="lg-field">
                            <label className="lg-label" htmlFor="lg-email">Email</label>
                            <div className="lg-input-wrap">
                                <Mail size={16} className="lg-input-icon" />
                                <input
                                    id="lg-email"
                                    type="email"
                                    className="lg-input"
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    placeholder="prenom.nom@galis.fr"
                                    autoComplete="username"
                                    autoFocus
                                    required
                                />
                            </div>
                        </div>

                        <div className="lg-field">
                            <label className="lg-label" htmlFor="lg-password">Mot de passe</label>
                            <div className="lg-input-wrap">
                                <Lock size={16} className="lg-input-icon" />
                                <input
                                    id="lg-password"
                                    type={showPassword ? 'text' : 'password'}
                                    className="lg-input lg-input--password"
                                    name="mot_de_passe"
                                    value={form.mot_de_passe}
                                    onChange={handleChange}
                                    placeholder="••••••••"
                                    autoComplete="current-password"
                                    required
                                />
                                <button
                                    type="button"
                                    className="lg-eye"
                                    onClick={() => setShowPassword((v) => !v)}
                                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        <button type="submit" className="lg-submit" disabled={loading}>
                            {loading ? (
                                <span className="lg-loading">
                                    <span className="lg-spinner" />
                                    Connexion…
                                </span>
                            ) : (
                                <span className="lg-loading">
                                    Se connecter
                                    <ArrowRight size={17} />
                                </span>
                            )}
                        </button>
                    </form>

                    <div className="lg-footer">
                        © {new Date().getFullYear()} GALIS — S.P.O.T
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Login;
